import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
  Signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { skip } from "rxjs";
import { EnrichmentDataService } from "../../../services/enrichment-data.service";

/**
 * Tracks whether the enrichment editor has unsaved edits relative to the last
 * load/save. Encapsulates the `initialLoadComplete` flag pattern that lived
 * inline in `EnrichmentEditPackage`:
 *
 * - `enrichment$` and `popServiceDisplayDirty$` emit during the initial load
 *   (the HTTP `next()` calls `setEnrichment` and seeds POP serviceDisplay).
 *   Those emissions must NOT flip the dirty flag.
 * - After the load completes, every emission is treated as a user edit and
 *   sets `hasUnsavedEdits = true`.
 *
 * Use:
 *
 * ```ts
 * private readonly dirty = inject(EnrichmentDirtyTracker);
 *
 * // Wire subscriptions once on init:
 * this.dirty.startTracking();
 *
 * // Around an HTTP load:
 * this.dirty.beginLoad();
 * loadPackage().subscribe(() => this.dirty.endLoad());
 *
 * // After a successful save, clear the flag without re-tripping the gate:
 * this.dirty.markSaved();
 * ```
 */
/** Lifecycle states surfaced by the auto-save indicator (Phase 4.2). */
export type SaveState = "idle" | "dirty" | "saving" | "saved";

@Injectable()
export class EnrichmentDirtyTracker {
  private readonly destroyRef = inject(DestroyRef);
  private readonly data = inject(EnrichmentDataService);

  private initialLoadComplete = false;
  private readonly hasUnsavedEdits = signal(false);

  // ── Save indicator state (Phase 4.2) ────────────────────────────────────
  // Surfaces a lightweight "Saved 2 min ago" pill in the hero so marketing
  // can see the editor is healthy without watching for toasts. State is
  // derived from the existing dirty flag plus explicit lifecycle calls
  // from the save coordinator — no separate auto-save loop yet.
  private readonly lastSavedAtMs = signal<number | null>(null);
  private readonly isSaving = signal(false);

  /** Public read-only signal — true once the user has made any edit since
   *  the last load/save. */
  readonly isDirty: Signal<boolean> = computed(() => this.hasUnsavedEdits());

  /** Timestamp (ms epoch) of the last successful save, or null. */
  readonly lastSavedAt: Signal<number | null> = computed(() =>
    this.lastSavedAtMs(),
  );

  /** Combined indicator surfaced to the hero. Priority: saving → dirty →
   *  saved (with timestamp) → idle. */
  readonly saveState: Signal<SaveState> = computed(() => {
    if (this.isSaving()) return "saving";
    if (this.hasUnsavedEdits()) return "dirty";
    if (this.lastSavedAtMs() !== null) return "saved";
    return "idle";
  });

  /** Emits true on every dirty-flip; used by EnrichmentEditPackage to switch
   *  the package status badge from `published` → `modified`. */
  private readonly onDirtyChangeListeners: Array<() => void> = [];

  /** Register a callback fired the first time dirty flips to true since the
   *  last reset. Used by the edit-package shell to update package status. */
  onDirty(cb: () => void): void {
    this.onDirtyChangeListeners.push(cb);
  }

  /** Subscribe to enrichment$ + popServiceDisplayDirty$. Idempotent; safe to
   *  call once from the shell's ngOnInit. */
  startTracking(): void {
    // skip(1) drops the BehaviorSubject's replay so a fresh subscription
    // doesn't immediately mark dirty.
    this.data.enrichment$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.initialLoadComplete) return;
        this.flipDirty();
      });

    this.data.popServiceDisplayDirty$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((map) => {
        if (!this.initialLoadComplete) return;
        const anyDirty = Object.values(map).some((v) => v);
        if (!anyDirty) return;
        this.flipDirty();
      });
  }

  /** Call before kicking off a package load. Suppresses dirty-flips while
   *  the load streams in. */
  beginLoad(): void {
    this.initialLoadComplete = false;
    this.hasUnsavedEdits.set(false);
    this.lastSavedAtMs.set(null);
    this.isSaving.set(false);
  }

  /** Call once the load's HTTP `next()` has finished. Future emissions on
   *  enrichment$ / popServiceDisplayDirty$ now count as user edits. */
  endLoad(): void {
    this.initialLoadComplete = true;
  }

  /** Mark that a save HTTP call is in flight so the indicator can show a
   *  spinner pill. Pair with `markSaved` (success) or `markSaveCancelled`
   *  (failure) so the state doesn't get stuck. */
  markSaving(): void {
    this.isSaving.set(true);
  }

  /** Clear the saving flag without flipping into "Saved" — for cancelled
   *  / failed saves. */
  markSaveCancelled(): void {
    this.isSaving.set(false);
  }

  /** Call after a successful save: clears the dirty flag without disabling
   *  the gate (so subsequent edits keep flipping it back to dirty). */
  markSaved(): void {
    this.hasUnsavedEdits.set(false);
    this.isSaving.set(false);
    this.lastSavedAtMs.set(Date.now());
  }

  /** Call when the user discards unsaved edits (Cancel → "Discard edits"):
   *  clears the dirty flag so the route CanDeactivate guard doesn't re-prompt
   *  on the way out. Unlike {@link markSaved} it does NOT stamp a save time —
   *  nothing was persisted. (#52) */
  markDiscarded(): void {
    this.hasUnsavedEdits.set(false);
    this.isSaving.set(false);
  }

  private flipDirty(): void {
    if (this.hasUnsavedEdits()) return;
    this.hasUnsavedEdits.set(true);
    for (const cb of this.onDirtyChangeListeners) cb();
  }
}
